/**
 * "Suggest for the visible backlog" (tracker item 41, phase 4): a greedy
 * plan for the backlog rows on screen (schedulable ones, at most 25) from
 * POST /dispatch/suggestions/bulk. Each placement already accounts for the
 * ones above it, so the plan never double-books. Nothing is written until
 * "Schedule selected", which saves the ticked rows one by one through the
 * normal schedule path with requireNoConflicts; a row whose slot was taken
 * meanwhile is skipped and counted. Office roles only (route-gated page).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { IDispatchApi } from '../api';
import * as types from '../api/types';
import { getErrorMessage } from '../utils/apiError';
import { primaryButtonClass, secondaryButtonClass } from './formStyles';
import { formatDayHeading, formatDuration } from '../pages/dispatchUi';
import { addedTravelText, candidateTimeText, excludedText, suggestionReasonText } from '../pages/suggestionUi';
import { BulkApplyResult, applyBulkPlacements, placementText, unplacedText } from '../pages/travelUi';
import { Toast } from './Toast';

interface BulkSuggestDialogProps {
  /** The visible, schedulable backlog rows (at most 25), in the order shown */
  jobs: types.BoardJob[];
  /** More schedulable rows are loaded than the 25 sent */
  more?: boolean;
  api: Pick<IDispatchApi, 'getBulkSuggestions' | 'checkJobSchedule' | 'scheduleJob'>;
  onApplied: (result: BulkApplyResult) => void;
  onClose: () => void;
}

export function BulkSuggestDialog({ jobs, more, api, onApplied, onClose }: BulkSuggestDialogProps) {
  const [anyTechnician, setAnyTechnician] = useState(false);
  const [anyDate, setAnyDate] = useState(false);
  const [plan, setPlan] = useState<types.BulkSuggestionResult | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState<{ done: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const panel = useRef<HTMLDivElement>(null);
  const closeButton = useRef<HTMLButtonElement>(null);
  const jobIds = useMemo(() => jobs.map((j) => j.id), [jobs]);
  const jobById = useMemo(() => new Map(jobs.map((j) => [j.id, j])), [jobs]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const next = await api.getBulkSuggestions({ jobIds, anyTechnician, anyDate });
      setPlan(next);
      setSelected(new Set(next.placements.filter((p) => p.candidate).map((p) => p.jobId)));
    } catch (err) {
      setPlan(null);
      setError(getErrorMessage(err, 'Could not plan the backlog'));
    } finally {
      setLoading(false);
    }
  }, [api, jobIds, anyTechnician, anyDate]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const origin = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    closeButton.current?.focus();
    return () => origin?.focus();
  }, []);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !applying) onClose();
      if (event.key !== 'Tab') return;
      const focusable = panel.current?.querySelectorAll<HTMLElement>('button:not([disabled]), input:not([disabled])');
      if (!focusable?.length) return;
      const first = focusable[0], last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, applying]);

  const toggle = (jobId: string) => setSelected((current) => {
    const next = new Set(current);
    if (next.has(jobId)) next.delete(jobId); else next.add(jobId);
    return next;
  });

  const apply = async () => {
    if (!plan) return;
    const chosen = plan.placements.filter((p) => p.candidate && selected.has(p.jobId));
    setApplying({ done: 0, total: chosen.length });
    try {
      const result = await applyBulkPlacements(api, chosen, jobById, (done, total) => setApplying({ done, total }));
      onApplied(result);
    } finally {
      setApplying(null);
    }
  };

  const busy = loading || applying !== null;
  const chosenCount = plan ? plan.placements.filter((p) => p.candidate && selected.has(p.jobId)).length : 0;
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4" role="presentation">
      <div ref={panel} role="dialog" aria-modal="true" aria-labelledby="bulk-suggest-title"
        className="max-h-full w-full overflow-y-auto rounded-t-lg bg-white p-5 shadow-xl sm:max-w-2xl sm:rounded-lg">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h2 id="bulk-suggest-title" className="text-lg font-semibold text-gray-900">Suggest for the visible backlog</h2>
            <p className="text-sm text-gray-600">
              {jobs.length} job{jobs.length === 1 ? '' : 's'}{more ? ' (the first 25 shown)' : ''}, placed one after another in backlog order: each slot accounts for the ones above it. Nothing is saved until you schedule.
            </p>
          </div>
          <button ref={closeButton} type="button" className={secondaryButtonClass} disabled={applying !== null} onClick={onClose}>Close</button>
        </div>

        <fieldset className="mt-3 flex flex-wrap gap-4 text-sm text-gray-700">
          <legend className="sr-only">Search wider</legend>
          <label className="inline-flex items-center gap-2">
            <input type="checkbox" checked={anyTechnician} disabled={busy} onChange={(event) => setAnyTechnician(event.target.checked)} />
            Any technician (not only the assigned one)
          </label>
          <label className="inline-flex items-center gap-2">
            <input type="checkbox" checked={anyDate} disabled={busy} onChange={(event) => setAnyDate(event.target.checked)} />
            Any date (not only the job's date)
          </label>
        </fieldset>

        {error && <Toast kind="error">{error}</Toast>}
        {loading && <p role="status" className="mt-4 text-sm text-gray-600">Planning the backlog…</p>}
        {!loading && plan && (
          <>
            <p className="mt-3 text-xs text-gray-500">
              {plan.from === plan.to ? formatDayHeading(plan.from) : `${formatDayHeading(plan.from)} to ${formatDayHeading(plan.to)}`}
              {' · '}{plan.placed} placed, {plan.unplaced} not placed · Travel is estimated from straight-line distance. Map data © OpenStreetMap contributors.
            </p>
            {plan.truncated && <p className="mt-2 text-xs text-amber-800">Only the first 50 technicians were checked for some jobs.</p>}
            <ol className="mt-3 space-y-2" aria-label="Planned placements">
              {plan.placements.map((placement, index) => {
                const candidate = placement.candidate;
                return (
                  <li key={placement.jobId} className="rounded border border-gray-200 p-3" data-placement={placement.jobId}>
                    <div className="flex items-start gap-3">
                      {candidate ? (
                        <input type="checkbox" className="mt-1" checked={selected.has(placement.jobId)} disabled={busy}
                          aria-label={`Schedule ${placement.customerName}`} onChange={() => toggle(placement.jobId)} />
                      ) : <span className="mt-1 inline-block w-[13px]" aria-hidden="true" />}
                      <div className="min-w-0 flex-1">
                        <div className="break-words font-semibold text-gray-900">{index + 1}. {placement.customerName}</div>
                        <div className="truncate text-xs text-gray-600">{placement.address} · {formatDuration(placement.durationMinutes)}</div>
                        {candidate ? (
                          <>
                            <div className="text-sm text-gray-800">{placementText(candidate, candidateTimeText(candidate))}</div>
                            <div className="text-xs font-medium text-gray-600">{addedTravelText(candidate)}</div>
                            {candidate.reasons.length > 0 && (
                              <ul className="mt-1 list-disc space-y-0.5 pl-5 text-xs text-gray-600">
                                {candidate.reasons.map((reason, i) => <li key={i}>{suggestionReasonText(reason)}</li>)}
                              </ul>
                            )}
                          </>
                        ) : (
                          <div className="text-sm text-amber-800" data-unplaced={placement.reason ?? ''}>
                            {unplacedText(placement)}{placement.excluded ? ` ${excludedText(placement.excluded)}` : ''}
                          </div>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>
            <p className="mt-3 text-xs text-gray-500">Unticking a job keeps the others' times; their drive estimates may then be a little off.</p>
            <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
              {applying && <span role="status" className="text-sm text-gray-600">Scheduling {applying.done} of {applying.total}…</span>}
              <button type="button" className={primaryButtonClass} disabled={busy || chosenCount === 0} onClick={apply}>
                Schedule {chosenCount} selected
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
